/**
 * UniData для SwingTreeTable.reloadNode → POST platform/reload/element / ответ js-request.
 */
import { IKey } from '@/common/constants/ikey'
import { mergePageFieldMaps } from '@/common/utils/mergePageFieldMaps'
import { fieldParticipatesInTreeTableSearchCriteria } from '@/common/utils/treeTableSearch'
import { setType } from '@/common/utils/typedValueSerializer'

export type FlatVisibleTreeRow = {
  key: string
  parentKey: string | null
  data: Record<string, any>
}

/** Полное дерево: rowNum === -1 или нет валидной строки. */
export function resolveSwingReloadNodeTarget(
  flatVisible: FlatVisibleTreeRow[],
  rowNum: number,
  needReloadParent: boolean,
): { kind: 'full' } | { kind: 'row'; rowNum: number; row: Record<string, any> } {
  if (rowNum === -1) {
    return { kind: 'full' }
  }
  if (!flatVisible.length || rowNum < 0 || rowNum >= flatVisible.length) {
    return { kind: 'full' }
  }

  let effectiveIdx = rowNum
  let entry = flatVisible[effectiveIdx]

  if (needReloadParent) {
    if (!entry.parentKey) {
      return { kind: 'full' }
    }
    const pIdx = flatVisible.findIndex((r) => r.key === entry.parentKey)
    if (pIdx < 0) {
      return { kind: 'full' }
    }
    const parent = flatVisible[pIdx]
    if (parent.parentKey == null) {
      return { kind: 'full' }
    }
    effectiveIdx = pIdx
    entry = parent
  }

  return { kind: 'row', rowNum: effectiveIdx, row: entry.data }
}

export function extractTreeTableServerChildCount(data: Record<string, any> | null | undefined): number | null {
  if (!data || typeof data !== 'object') return null
  const cc = (data as any)._countChildren ?? (data as any).countChildren
  if (cc != null && typeof cc === 'object' && 'value' in cc) {
    const n = Number((cc as { value: unknown }).value)
    return Number.isFinite(n) ? n : null
  }
  if (cc != null) {
    const n = Number(cc)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function emptyUniDataTableField(): { type: string; value: Record<string, unknown> } {
  return {
    type: 'UniDataTable',
    value: {
      rows: [],
      columns: [],
      attributes: {},
      empty: true,
    },
  }
}

export function buildFullTreeReloadPayload(elementName: string): Record<string, any> {
  return {
    [`${elementName}__${IKey.ROW_NUM}`]: setType(-1),
    [`${elementName}${IKey.PARAMS}`]: emptyUniDataTableField(),
    [`${elementName}${IKey.TT_INIT}`]: setType(true),
    [`${elementName}${IKey.TT_FORCE_LOAD_ALL}`]: setType(true),
  }
}

function coerceFlatPayloadFieldValue(field: unknown): unknown {
  if (field && typeof field === 'object' && !Array.isArray(field) && 'value' in field) {
    return (field as { value: unknown }).value
  }
  return field
}

function isTruthyFlatPayloadFlag(value: unknown): boolean {
  if (value === true) return true
  if (value === false || value == null) return false
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }
  if (typeof value === 'number') return value !== 0
  return Boolean(value)
}

export function isTreeTableSearchReloadPayload(
  elementName: string,
  flatPayload: Record<string, unknown> | null | undefined,
): boolean {
  const name = String(elementName ?? '').trim()
  if (!name || !flatPayload || typeof flatPayload !== 'object') return false
  const searchKey = `${name}${IKey.TT_SEARCH}`
  return isTruthyFlatPayloadFlag(coerceFlatPayloadFieldValue(flatPayload[searchKey]))
}

export function isLazyTreeTableRowExpandIntent(
  elementName: string,
  flatPayload: Record<string, unknown> | null | undefined,
): boolean {
  const name = String(elementName ?? '').trim()
  if (!name || !flatPayload || typeof flatPayload !== 'object') return false

  const searchKey = `${name}${IKey.TT_SEARCH}`
  if (isTruthyFlatPayloadFlag(coerceFlatPayloadFieldValue(flatPayload[searchKey]))) {
    return false
  }

  const paramsKey = `${name}${IKey.PARAMS}`
  if (!(paramsKey in flatPayload)) return false

  const rowNum = coerceFlatPayloadFieldValue(flatPayload[`${name}__${IKey.ROW_NUM}`])
  if (rowNum === -1 || rowNum === '-1') return false
  const n = typeof rowNum === 'string' ? Number(rowNum) : rowNum
  return typeof n === 'number' && Number.isFinite(n) && n >= 0
}

/** chainCalled / En.reloadElement: полная переинициализация дерева по flat или typed полям. */
export function isFullTreeReloadIntent(
  elementName: string,
  flatPayload: Record<string, unknown> | null | undefined,
): boolean {
  const name = String(elementName ?? '').trim()
  if (!name || !flatPayload || typeof flatPayload !== 'object') return false
  if (isTreeTableSearchReloadPayload(name, flatPayload)) return false
  if (isLazyTreeTableRowExpandIntent(name, flatPayload)) return false

  const initKey = `${name}${IKey.TT_INIT}`
  if (isTruthyFlatPayloadFlag(coerceFlatPayloadFieldValue(flatPayload[initKey]))) {
    return true
  }

  const rowNumKey = `${name}__${IKey.ROW_NUM}`
  const rowNum = coerceFlatPayloadFieldValue(flatPayload[rowNumKey])
  return rowNum === -1 || rowNum === '-1'
}

/** Накладывает buildFullTreeReloadPayload и пустую таблицу поверх  */
export function mergeFullTreeReloadIntoFlatFormData(
  elementName: string,
  base: Record<string, unknown>,
  overrides?: Record<string, unknown>,
): Record<string, unknown> {
  const name = String(elementName ?? '').trim()
  return {
    ...base,
    ...buildFullTreeReloadPayload(name),
    ...(overrides || {}),
    [name]: emptyUniDataTableField(),
  }
}

export function buildRowTreeReloadPayload(
  elementName: string,
  visibleRowNum: number,
  expandParamFields: Record<string, { value: unknown; type: string }>,
  childCount: number | null,
  rowChecked: boolean,
): Record<string, any> {
  const paramsTable = {
    type: 'UniDataTable',
    value: {
      rows: [{ fields: expandParamFields, errors: {}, attributes: {} }],
      columns: [] as unknown[],
      attributes: {},
      empty: Object.keys(expandParamFields).length === 0,
    },
  }

  const out: Record<string, any> = {
    [`${elementName}__${IKey.ROW_NUM}`]: setType(visibleRowNum),
    [`${elementName}__${IKey.ROW_CHECKED}`]: setType(rowChecked),
    [`${elementName}${IKey.PARAMS}`]: paramsTable,
  }
  if (childCount != null) {
    out[`${elementName}${IKey.TT_COUNT_CHILDREN}`] = setType(childCount)
  }
  return out
}

function buildTreeTableSearchSelectedClearFields(elementName: string): Record<string, unknown> {
  // Swing notFound: boolean false на _selected; пустая таблица на сервере path не сбрасывает.
  return {
    [`${elementName}${IKey.SELECTED}`]: setType(false),
    [`${elementName}__selected`]: setType(false),
  }
}

export function buildTreeTableSearchStickyClearFields(elementName: string): Record<string, unknown> {
  return {
    [`${elementName}${IKey.TT_SEARCH}`]: setType(false),
    [`${elementName}${IKey.TT_NEXT_SEARCH_POSITION}`]: setType(0),
    [`${elementName}__prevSearchPosition`]: setType(0),
    [`${elementName}__direction`]: setType(0),
    [`${elementName}${IKey.SHOW_MESSAGE_DIALOG}`]: setType(false),
    ...buildTreeTableSearchSelectedClearFields(elementName),
    [`${elementName}${IKey.TT_FORCE_LOAD_ALL}`]: setType(false),
    [`${elementName}${IKey.TT_INIT}`]: setType(false),
  }
}

function coercePlainScalarForTreeTableExpand(value: unknown): unknown {
  if (value !== null && typeof value === 'object' && 'value' in value) {
    return (value as { value: unknown }).value
  }
  return value
}

export function shouldOmitPageScalarForTreeTableExpand(
  key: string,
  value: unknown,
  options?: {
    tableName?: string
    pageFields?: Record<string, unknown>
    liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>
  },
): boolean {
  const tableName = String(options?.tableName ?? '').trim()
  const pageFields = options?.pageFields
  const liveOverrides = options?.liveOverrides
  if (
    tableName &&
    pageFields &&
    fieldParticipatesInTreeTableSearchCriteria(key, pageFields, tableName, { liveOverrides })
  ) {
    return true
  }
  const plain = coercePlainScalarForTreeTableExpand(value)
  if (plain === -1 || plain === '-1') return true
  return false
}

export function isForeignTreeTableAuxField(key: string, tableName: string): boolean {
  const prefix = String(tableName ?? '').trim()
  if (prefix && (key === prefix || key.startsWith(`${prefix}__`) || key.startsWith(`${prefix}_`))) {
    return false
  }
  const treeAuxMarkers = [
    '__search',
    '__initTreeTable',
    '__showMessageDialog',
    '__nextSearchPosition',
    '__prevSearchPosition',
    'countChildren',
    '_countChildren',
  ]
  return treeAuxMarkers.some((marker) => key.includes(marker))
}

function isForeignTableServiceField(key: string, tableName: string): boolean {
  const prefix = String(tableName ?? '').trim()
  if (prefix && key.startsWith(`${prefix}__`)) return false
  const suffixes = ['__page', '__maxOnPage', '__size', '__listbox']
  return suffixes.some((suffix) => key.endsWith(suffix) || key.includes(suffix))
}

export function sanitizePageFieldsForTreeTableExpand(
  pageFields: Record<string, unknown>,
  tableName: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(pageFields || {})) {
    if (key === '_id' || key === '_versionId' || key === '_typeId') continue
    if (key === 'ProcessInstances') continue
    if (key.startsWith('$')) continue
    if (key.endsWith('__action')) continue
    if (key.startsWith('label')) continue
    if (isForeignTreeTableAuxField(key, tableName)) continue
    if (isForeignTableServiceField(key, tableName)) continue
    out[key] = field
  }
  return out
}

export function buildTreeTableSearchProcedureContext(
  panelScalars: Record<string, unknown>,
  chainScalars: Record<string, unknown>,
  tableName: string,
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>,
): Record<string, unknown> {
  return flatExpandContextFromPageFields(
    mergePageFieldMaps(panelScalars, chainScalars),
    tableName,
    liveOverrides,
  )
}

export function flatExpandContextFromPageFields(
  pageFields: Record<string, unknown>,
  tableName: string,
  liveOverrides?: Record<string, { type?: string; value?: unknown } | undefined>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  const prefix = String(tableName ?? '').trim()
  if (!prefix) return out
  const sanitized = sanitizePageFieldsForTreeTableExpand(pageFields, tableName)
  for (const [key, field] of Object.entries(sanitized)) {
    if (key === prefix) continue
    if (key.startsWith(`${prefix}__`) || key.startsWith(`${prefix}_`)) continue
    if (shouldOmitPageScalarForTreeTableExpand(key, field, {
      tableName: prefix,
      pageFields: sanitized,
      liveOverrides,
    })) continue
    if (field == null) continue
    if (typeof field === 'object' && 'type' in field && 'value' in field) {
      const fieldType = String((field as { type?: string }).type ?? '')
      if (fieldType === 'UniDataTable' || fieldType === 'UniDataTableFields') continue
      out[key] = field
      continue
    }
    if (
      typeof field === 'string' ||
      typeof field === 'number' ||
      typeof field === 'boolean'
    ) {
      out[key] = setType(field)
    }
  }
  return out
}

export function buildTreeTableLazyExpandReloadPayload(
  elementName: string,
  visibleRowNum: number,
  expandParamFields: Record<string, { value: unknown; type: string }>,
  options?: { childCount?: number | null; rowChecked?: boolean },
): Record<string, any> {
  return {
    ...buildRowTreeReloadPayload(
      elementName,
      visibleRowNum,
      expandParamFields,
      options?.childCount ?? null,
      options?.rowChecked ?? false,
    ),
    // Перекрывают «липкие» флаги из pageFields (напр. tab onClick tbl1__forceLoadAll на форме 317).
    [`${elementName}${IKey.TT_FORCE_LOAD_ALL}`]: setType(false),
    [`${elementName}${IKey.TT_INIT}`]: setType(false),
    // Иначе platform трактует reload как search, если на session остался __search=true.
    [`${elementName}${IKey.TT_SEARCH}`]: setType(false),
  }
}

export function selectionKeysIndicatesChecked(
  selectionKeys: Record<string, unknown> | null | undefined,
  nodeKey: string,
): boolean {
  if (!selectionKeys || !nodeKey) return false
  const raw = selectionKeys[nodeKey]
  if (raw === true) return true
  if (raw && typeof raw === 'object' && 'checked' in (raw as object)) {
    return Boolean((raw as { checked?: boolean }).checked)
  }
  return false
}
